/*
 * One-click Google login helpers (Google Identity Services, ID-token flow).
 *
 * The same flow the popup already uses, minus the NAVI popup: Google's One Tap
 * returns an ID token, the page posts it to POST /api/auth/google and the
 * backend verifies it with Google and creates the session.
 */

const GIS_SRC = "https://accounts.google.com/gsi/client";
const SCRIPT_ID = "google-identity-services"; // same id the Login page / AuthModal use, so the script loads only once

export function loadGoogleIdentity() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google);
    const fail = () => reject(new Error("Could not load Google sign-in"));
    const done = () => (window.google?.accounts?.id ? resolve(window.google) : fail());
    let script = document.getElementById(SCRIPT_ID);
    if (script) {
      script.addEventListener("load", done, { once: true });
      script.addEventListener("error", fail, { once: true });
      return;
    }
    script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = done;
    script.onerror = fail;
    document.body.appendChild(script);
  });
}

/** Build-time env first, backend second (same order the popup uses). */
export async function getGoogleClientId(api) {
  if (process.env.REACT_APP_GOOGLE_CLIENT_ID) return process.env.REACT_APP_GOOGLE_CLIENT_ID;
  try {
    const { data } = await api.get("/auth/google/config");
    return data?.client_id || "";
  } catch {
    return "";
  }
}

/**
 * Ask Google for an ID token for a returning customer.
 * Resolves with the credential string, or null when Google cannot / will not show
 * One Tap (dismissed, cool-down, no Google session, blocked) so the caller can fall back.
 * auto_select lets Google sign the person in with no extra click when it is allowed to.
 */
export async function googleOneTapCredential(clientId, loginHint = "", timeoutMs = 15000) {
  const google = await loadGoogleIdentity();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => {
      try { google.accounts.id.cancel(); } catch {}
      finish(null);
    }, timeoutMs);

    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => finish(response?.credential || null),
      auto_select: true,
      cancel_on_tap_outside: false,
      itp_support: true,
      ...(loginHint ? { login_hint: loginHint } : {}),
    });

    google.accounts.id.prompt((n) => {
      try {
        if (n.isDismissedMoment && n.isDismissedMoment()) {
          if (n.getDismissedReason && n.getDismissedReason() !== "credential_returned") finish(null);
        } else if ((n.isNotDisplayed && n.isNotDisplayed()) || (n.isSkippedMoment && n.isSkippedMoment())) {
          finish(null);
        }
      } catch {
        /* FedCM builds can throw on the legacy notification getters; the timeout still covers us */
      }
    });
  });
}

/** After an explicit log out, stop Google from silently signing the person straight back in. */
export function stopGoogleAutoSelect() {
  try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch {}
}
